import type { IPrice } from '@/interface/IPrice';
import { BetBuilderTicketStatus, OpenTicketStatus, PlaceOrderStatus } from '@/interface/enum';
import { i18n } from '@/libraries/i18n';
import { formatMinDecimalPlace } from '@/libraries/utils/text';

export function getDynamicMarketOpenTicketErrorMessage(code: OpenTicketStatus, oldPrice: IPrice | null, newPrice: IPrice | null) {
    switch (code) {
        case OpenTicketStatus.InvalidInput:
        case OpenTicketStatus.InvalidPrice:
            return i18n.t('betSlip_errorMessage_generalFailure');
        case OpenTicketStatus.EventClosed:
        case OpenTicketStatus.EventCompleted:
        case OpenTicketStatus.EventInternal:
            return i18n.t('betSlip_errorMessage_eventClosed');
        case OpenTicketStatus.OddsClosed:
        case OpenTicketStatus.OddsCompleted:
        case OpenTicketStatus.OddsInternal:
            return i18n.t('betSlip_errorMessage_oddsClosed');
        case OpenTicketStatus.MaxOddsExceed:
            return i18n.t('betSlip_errorMessage_maxOddsExceed');
        default:
            return comparePriceMessage(oldPrice, newPrice);
    }
}

function comparePriceMessage(oldPrice: IPrice | null, newPrice: IPrice | null) {
    if (oldPrice === null || newPrice === null) {
        return '';
    }
    if (oldPrice.value !== newPrice.value) {
        return i18n.t('betSlip_errorMessage_oddsChanged', {
            oldOdds: formatMinDecimalPlace(oldPrice.value, 2),
            newOdds: formatMinDecimalPlace(newPrice.value, 2),
        });
    }
    return '';
}

const PlaceBetErrorMessageMap: Partial<Record<PlaceOrderStatus, string>> = {
    [PlaceOrderStatus.EventClosed]: 'betSlip_errorMessage_eventClosed',
    [PlaceOrderStatus.MemberSuspend]: 'betSlip_errorMessage_memberSuspend',
    [PlaceOrderStatus.HierarachySuspend]: 'betSlip_errorMessage_hierarachySuspend',
    [PlaceOrderStatus.InsufficientCredit]: 'betSlip_errorMessage_insufficientCredit',
    [PlaceOrderStatus.InBlackList]: 'betSlip_errorMessage_generalFailure',
    [PlaceOrderStatus.InvalidPrice]: 'betSlip_errorMessage_invalidPrice',
    [PlaceOrderStatus.PriceSuspend]: 'betSlip_errorMessage_priceSuspend',
    [PlaceOrderStatus.PriceChange]: 'betSlip_errorMessage_priceChange',
    [PlaceOrderStatus.HdpPointChange]: 'betSlip_errorMessage_hdpPointChange',
    [PlaceOrderStatus.AccountProblem]: 'betSlip_errorMessage_accountProblem',
    [PlaceOrderStatus.MaxBetExceed]: 'betSlip_errorMessage_maxBetExceed',
    [PlaceOrderStatus.MinBetUnMet]: 'betSlip_errorMessage_minBetUnMet',
    [PlaceOrderStatus.MaxPerMatchExceed]: 'betSlip_errorMessage_maxPerMatchExceed',
    [PlaceOrderStatus.SportExcluded]: 'betSlip_errorMessage_sportExcluded',
    [PlaceOrderStatus.BettingBudgetExceed]: 'betSlip_errorMessage_bettingBudgetExceed',
    [PlaceOrderStatus.MPMaxPayoutExceed]: 'betSlip_errorMessage_mpMaxPayoutExceed',
    [PlaceOrderStatus.MPMaxAccumulatedStakeForSubEventExceed]: 'betSlip_errorMessage_mpMaxAccumulatedStakeForSubEventExceed',
    [PlaceOrderStatus.InvalidVoucher]: 'betSlip_errorMessage_invalidVoucher',
    [PlaceOrderStatus.InvalidIp]: 'betSlip_errorMessage_invalidIp',
    [PlaceOrderStatus.CountryIsBlock]: 'betSlip_errorMessage_countryIsBlock',
    [PlaceOrderStatus.PhoneBetting]: 'betSlip_errorMessage_phoneBettingOnly',
    [PlaceOrderStatus.ThirdPartyTimeout]: 'betSlip_errorMessage_thirdPartyTimeout',
    [PlaceOrderStatus.LimitExceedTryLater]: 'betSlip_errorMessage_limitExceedTryLater',
    [PlaceOrderStatus.LimitExceed]: 'betSlip_errorMessage_limitExceed',
    [PlaceOrderStatus.GeneralFailure]: 'betSlip_errorMessage_generalFailure',
    [PlaceOrderStatus.RetryOpenTicket]: 'betSlip_errorMessage_generalFailure',
    [PlaceOrderStatus.PTProblem]: 'betSlip_errorMessage_accountProblem',
    [PlaceOrderStatus.InvalidRefNo]: 'betSlip_errorMessage_generalFailure',
    [PlaceOrderStatus.InvalidCurrencyForVietnam]: 'betSlip_errorMessage_accountProblem',
    [PlaceOrderStatus.LessEvent]: 'betSlip_errorMessage_lessThanThreeBets',
    [PlaceOrderStatus.NotValidMixParlayCombination]: 'betSlip_errorMessage_notValidMixParlayCombination',
    [PlaceOrderStatus.BetBuilderMaxPayoutExceed]: 'betSlip_errorMessage_betBuilderMaxPayoutExceed',
    [PlaceOrderStatus.BetBuilderMaxAccumulatedStakeExceed]: 'betSlip_errorMessage_betBuilderMaxAccumulatedStakeExceed',
    [PlaceOrderStatus.LessSelection]: 'betSlip_errorMessage_insufficientLegs',
    [PlaceOrderStatus.OrderProcessing]: 'betSlip_errorMessage_orderProcessing',
    [PlaceOrderStatus.ScoreChanged]: 'bet_slip_error_message_score_changed',
};

export function getPlaceBetErrorMessage(status: PlaceOrderStatus) {
    return PlaceBetErrorMessageMap[status] ?? 'betSlip_errorMessage_generalFailure';
}

const BetBuilderOpenTicketErrorMessageMap: Partial<Record<BetBuilderTicketStatus, string>> = {
    [BetBuilderTicketStatus.Unknown]: 'betSlip_errorMessage_generalFailure',
    [BetBuilderTicketStatus.MaxLegCountExceeded]: 'betSlip_errorMessage_maxLegCountExceeded',
    [BetBuilderTicketStatus.RedundantSelection]: 'bet_slip_error_message_redundant_selection',
    [BetBuilderTicketStatus.Unavailable]: 'betSlip_errorMessage_unavailable',
    [BetBuilderTicketStatus.IncompatibleSelection]: 'bet_slip_error_message_conflict_selection',
};

export function getBetBuilderOpenTicketErrorMessage(status: BetBuilderTicketStatus) {
    return BetBuilderOpenTicketErrorMessageMap[status] ?? 'betSlip_errorMessage_generalFailure';
}
